import "@/i18n/config";
import React, { useEffect, useMemo, useState } from "react";
import SVG from "react-inlinesvg";

import cancelURL from "@assets/icons/cancel.svg";
import editURL from "@assets/icons/edit.svg";
import saveURL from "@assets/icons/save.svg";

import {
  getPerformanceFields,
  getPerformanceRecords,
  savePerformanceRecords,
} from "@/client_app/components/CommunityMemberDetails/services/FetchAPIService";
import { PrimaryButton } from "@/ui/buttons";

interface Props {
  positionId: string;
  inAdmin?: boolean;
}

interface PerformanceRecord {
  id: string;
  date: string;
  metrics: object;
  values: object;
  isEditing: boolean;
}

interface Field {
  field_name: string;
  display_name: string;
}
export function Performance(props: Props) {
  const { positionId, inAdmin } = props;
  const [performanceData, setPerformanceData] = useState<PerformanceRecord[]>(
    []
  );
  const [backupData, setBackupData] = useState<PerformanceRecord[]>([]);
  const [fields, setFields] = useState<Field[]>([]);
  const [rowWarnings, setRowWarnings] = useState<Record<number, string>>({});

  useEffect(() => {
    getPerformanceFields(positionId, inAdmin)
      .then((response) => response.json())
      .then((data) => {
        setFields(data.fields);
      });
    if (positionId) {
      getPerformanceRecords(positionId, inAdmin)
        .then((response) => response.json())
        .then((data) => {
          setPerformanceData(data.records);
        });
    }
  }, [positionId]);

  const toggleEditMode = (index: number, enable: boolean) => {
    const updatedData = [...performanceData];
    updatedData[index] = {
      ...updatedData[index],
      isEditing: enable,
    };

    if (enable) {
      // Save a backup of the row data when entering edit mode
      setBackupData((prev) => ({
        ...prev,
        [index]: { ...performanceData[index] },
      }));
    } else {
      // Clear the backup when exiting edit mode
      setBackupData((prev) => {
        const updatedBackup = { ...prev };
        delete updatedBackup[index];
        return updatedBackup;
      });

      setRowWarnings((prev) => {
        const updatedWarnings = { ...prev };
        delete updatedWarnings[index];
        return updatedWarnings;
      });
    }

    setPerformanceData(updatedData);
  };

  const handleInputChange = (index: number, key: string, value: any) => {
    const updatedData = [...performanceData];
    if (key == "date") {
      updatedData[index] = { ...updatedData[index], date: value };
    } else {
      updatedData[index] = {
        ...updatedData[index],
        values: { ...updatedData[index].values, [key]: value },
      };
    }
    setPerformanceData(updatedData);

    // Clear warnings if date changes
    if (key === "date" && rowWarnings[index]) {
      setRowWarnings((prev) => {
        const updatedWarnings = { ...prev };
        delete updatedWarnings[index];
        return updatedWarnings;
      });
    }
  };

  const saveRow = (index: number) => {
    const { date, values, id } = performanceData[index];

    if (!date) {
      setRowWarnings((prev) => ({
        ...prev,
        [index]: "Date is required",
      }));
      return;
    }

    savePerformanceRecords(positionId, date, id, values, inAdmin)
      .then((response) => response.json())
      .then((data) => {
        setPerformanceData((prevData) => {
          const updatedData = [...prevData];
          updatedData[index] = {
            ...updatedData[index],
            ...data.record,
            isEditing: false,
          };
          return updatedData;
        });
      });
  };
  const cancelEdit = (index: number) => {
    const isNewRow = !performanceData[index]?.id;
    if (isNewRow) {
      // Remove the new row entirely if it's canceled
      const updatedData = performanceData.filter((_, i) => i !== index);
      setPerformanceData(updatedData);
    } else {
      // Revert to the original data for existing rows
      const updatedData = [...performanceData];
      updatedData[index] = { ...backupData[index], isEditing: false }; // Restore original data and exit edit mode
      setPerformanceData(updatedData);
      setBackupData((prev) => {
        const updatedBackup = { ...prev };
        delete updatedBackup[index];
        return updatedBackup;
      });
    }
  };

  const addRow = () => {
    setPerformanceData((prev) => [
      {
        date: "",
        id: "",
        isEditing: true,
        metrics: defaultFields,
        values: defaultFields,
      },
      ...prev,
    ]);
  };

  const defaultFields = useMemo(() => {
    const row = {};
    fields.forEach((field) => {
      row[field.field_name] = 0;
    });

    return row;
  }, fields);

  return (
    <div>
      {!inAdmin && (
        <div className='flex justify-end pb-4'>
          <PrimaryButton label='Add New' onClick={addRow} />
        </div>
      )}
      <table className='w-full text-left table-auto border-neutral-200 border-2'>
        <thead className='bg-primary-50'>
          <tr>
            <th className='border-neutral-200 border'>Date</th>
            {fields.map(({ display_name }, index) => (
              <th
                className='border-neutral-200 border min-w-[50px]'
                key={index}
              >
                {display_name}
              </th>
            ))}
            {!inAdmin && <th className='border-neutral-200 border'>Action</th>}
          </tr>
        </thead>
        <tbody>
          {performanceData.map(
            ({ date, metrics, values, isEditing }, index) => (
              <>
                <tr key={index} className={index % 2 ? "" : "bg-neutral-50"}>
                  <td className='border-neutral-200 border'>
                    {isEditing ? (
                      <div>
                        <input
                          type='date'
                          value={date}
                          onChange={(e) =>
                            handleInputChange(index, "date", e.target.value)
                          }
                          className={
                            rowWarnings[index]
                              ? "form-control border-error-600"
                              : "form-control"
                          }
                        />
                      </div>
                    ) : (
                      new Date(date).toLocaleDateString("en-GB")
                    )}
                  </td>
                  {fields.map(({ field_name }) => (
                    <td className='border-neutral-200 border'>
                      {isEditing ? (
                        <input
                          type='number'
                          min='0'
                          value={values[field_name]}
                          onChange={(e) =>
                            handleInputChange(index, field_name, e.target.value)
                          }
                          className='form-control'
                        />
                      ) : (
                        <div>{metrics[field_name]}</div>
                      )}
                    </td>
                  ))}
                  {!inAdmin && (
                    <td className='border-neutral-200 border'>
                      {isEditing ? (
                        <div className='flex gap-2'>
                          <div className='tooltip border-0 cursor-pointer'>
                            <SVG
                              src={saveURL}
                              className='icon-sm fill-current ml-2 cursor-pointer'
                              onClick={() => saveRow(index)}
                            />
                            <span className='tooltiptext min-w-[50px] ml-0 left-0'>
                              Save
                            </span>
                          </div>
                          <div className='tooltip border-0 cursor-pointer'>
                            <SVG
                              src={cancelURL}
                              className='icon-sm fill-current ml-2 cursor-pointer red mr-1'
                              onClick={() => cancelEdit(index)}
                            />
                            <span className='tooltiptext min-w-[50px] ml-0 left-0'>
                              Cancel
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className='tooltip border-0 cursor-pointer'>
                          <SVG
                            src={editURL}
                            className='icon-sm fill-current ml-2 cursor-pointer'
                            onClick={() => toggleEditMode(index, true)}
                          />
                          <span className='tooltiptext min-w-[50px] ml-0 left-0'>
                            Edit
                          </span>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              </>
            )
          )}
        </tbody>
      </table>
    </div>
  );
}
